/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

/* eslint-disable no-duplicate-imports */
import {
    CacheType,
    createObservableDataAction,
    IAction,
    IActionContext,
    IActionInput,
    IAny,
    ICreateActionContext,
    IGeneric
} from '@msdyn365-commerce/core';
import { SortOption } from '../common/sort-option';

/**
 * Gets the sort options.
 * @returns - List of sort options.
 */
const getAllSortOptions = () => async (ctx: IActionContext): Promise<SortOption[]> => {
    return Promise.resolve([
        SortOption.CreatedDateTimeDescending,
        SortOption.CreatedDateTimeAscending,
        SortOption.ConfirmedShipDateDescending,
        SortOption.ConfirmedShipDateAscending
    ]);
};

/**
 * Gets the sort options without confirmed ship date options.
 * @returns - List of sort options.
 */
const getSortOptionsWithoutConfirmedShipDate = () => async (ctx: IActionContext): Promise<SortOption[]> => {
    return Promise.resolve([SortOption.CreatedDateTimeDescending, SortOption.CreatedDateTimeAscending]);
};

/**
 * Action input.
 */
export class GetSortOptionsInput implements IActionInput {
    public allowSortingByConfirmedShipDate: boolean;

    constructor(allowSortingByConfirmedShipDate: boolean) {
        this.allowSortingByConfirmedShipDate = allowSortingByConfirmedShipDate;
    }

    public getCacheKey = () => 'OrderHistoryWithSearchAndFiltersSortOptions';

    public getCacheObjectType = () => 'OrderHistoryWithSearchAndFiltersSortOptions';

    public dataCacheType = (): CacheType => 'instance';
}

/**
 * Creates the input required to make the retail api call.
 * @param inputData - Action Data Input.
 * @returns - Input object.
 */
const createGetSortOptionsInput = (inputData: ICreateActionContext<IGeneric<IAny>>): IActionInput => {
    return new GetSortOptionsInput(inputData.config?.showConfirmedShipDateSortingOptions || false);
};

/**
 * Get action.
 * @param input - Input data.
 * @param context - Action context.
 * @returns - List of sort options.
 */
export async function getSortOptionsAction(input: GetSortOptionsInput, context: IActionContext): Promise<SortOption[]> {
    if (!context) {
        throw new Error('getSortOptionsAction - Action context cannot be null/undefined');
    }

    let sortOptions: SortOption[] = [];

    try {
        sortOptions = input.allowSortingByConfirmedShipDate
            ? await getAllSortOptions()(context)
            : await getSortOptionsWithoutConfirmedShipDate()(context);
    } catch (error) {
        throw error;
    }

    return sortOptions;
}

/**
 * Get sort options action.
 * @param id - Unique Id for data action.
 * @param context - Action context.
 * @param input - Input for data action.
 * @returns Api response.
 */
export const getSortOptionsActionDataAction = createObservableDataAction<SortOption[]>({
    id: '@msdyn365-commerce-modules/order-history-with-search-and-filters/get-sort-options',
    action: getSortOptionsAction as IAction<SortOption[]>,
    input: createGetSortOptionsInput
});

export default getSortOptionsActionDataAction;
